/** Fecha o painel lateral do RealtimeKit (chat, enquetes, participantes, plugins). */

export type RtkUiStates = {
  activeSidebar?: boolean;
  sidebar?: string;
};

const SIDEBAR_SELECTOR = "rtk-sidebar, dyte-sidebar";
const MEETING_SELECTOR = "rtk-meeting, dyte-meeting";
const STATE_EVENTS = ["rtkStateUpdate", "dyteStateUpdate"] as const;
export const RTK_STATES_EVENTS = ["rtkStatesUpdate", "dyteStatesUpdate"] as const;

export function findRtkMeeting(root?: ParentNode | Document | null): HTMLElement | null {
  const scope = root ?? document;
  return (
    (scope.querySelector?.(MEETING_SELECTOR) as HTMLElement | null) ??
    document.querySelector(MEETING_SELECTOR)
  );
}

function queryIncludingShadow(scope: ParentNode, selector: string): HTMLElement | null {
  const light = scope.querySelector?.(selector) as HTMLElement | null;
  if (light) {
    return light;
  }
  const hosts = scope.querySelectorAll?.("*");
  if (!hosts) {
    return null;
  }
  for (let i = 0; i < hosts.length; i += 1) {
    const shadow = (hosts[i] as HTMLElement).shadowRoot;
    if (!shadow) {
      continue;
    }
    const found = queryIncludingShadow(shadow, selector);
    if (found) {
      return found;
    }
  }
  return null;
}

export function findRtkSidebar(root?: ParentNode | Document | null): HTMLElement | null {
  const scope = root ?? document;
  return queryIncludingShadow(scope, SIDEBAR_SELECTOR) ?? queryIncludingShadow(document, SIDEBAR_SELECTOR);
}

export function isRtkSidebarVisible(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  if (el.hasAttribute("hidden") || el.getAttribute("aria-hidden") === "true") {
    return false;
  }
  const style = window.getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
    return false;
  }
  return el.getClientRects().length > 0;
}

function isRtkSidebarMounted(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  if (el.hasAttribute("hidden") || el.getAttribute("aria-hidden") === "true") {
    return false;
  }
  const style = window.getComputedStyle(el);
  return style.display !== "none" && style.visibility !== "hidden";
}

export function isRtkSidebarOpen(root?: ParentNode | Document | null): boolean {
  const sidebar = findRtkSidebar(root);
  if (sidebar) {
    // O RTK tira o rtk-sidebar da árvore ao fechar. No celular ele fica no
    // shadow root do rtk-meeting, então getClientRects pode falhar e o
    // botão "Voltar" sumia com o chat ainda aberto.
    return isRtkSidebarMounted(sidebar);
  }
  const meetingEl = findRtkMeeting(root) as (HTMLElement & { states?: RtkUiStates }) | null;
  return Boolean(meetingEl?.states?.activeSidebar);
}

function emitClose(target: EventTarget): void {
  const detail: RtkUiStates = { activeSidebar: false, sidebar: undefined };
  for (const type of STATE_EVENTS) {
    target.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }
}

/**
 * Pede ao UI Kit para fechar o sidebar. O close nativo no celular some atrás
 * da barra Cenário/Videochamada ou é cortado pelos quatro abas do próprio RTK.
 */
export function closeRtkSidebar(root?: ParentNode | Document | null): boolean {
  const meetingEl = findRtkMeeting(root);
  const sidebar = findRtkSidebar(root);
  if (!meetingEl && !sidebar) {
    return false;
  }
  if (meetingEl) {
    emitClose(meetingEl);
  }
  if (sidebar) {
    emitClose(sidebar);
  }
  return true;
}

export function readActiveSidebarFromEvent(event: Event): boolean | undefined {
  const detail = (event as CustomEvent<RtkUiStates | undefined>).detail;
  if (!detail || typeof detail.activeSidebar !== "boolean") {
    return undefined;
  }
  return detail.activeSidebar;
}
